import { getCognitoCookieLogData, shipError } from '@built/app-auth-utility';
import { error, verbose, warn } from '@built/core-utility';

const prodMessengerConfig = {
  B4C: {
    salesForceKey: '00D1a000000IaVY',
    messagingChannel: 'B4C_Messaging_2',
    scrt2URL: 'https://getbuilt.my.salesforce-scrt.com',
    salesForceEmbeddedServiceURL:
      'https://getbuilt.my.site.com/ESWB4CMessaging21711060211981',
    eswBootstrapScriptSrc:
      'https://getbuilt.my.site.com/ESWB4CMessaging21711060211981/assets/js/bootstrap.min.js',
  },
  B4L: {
    salesForceKey: '00D1a000000IaVY',
    messagingChannel: 'B4L_Messaging',
    scrt2URL: 'https://getbuilt.my.salesforce-scrt.com',
    salesForceEmbeddedServiceURL:
      'https://getbuilt.my.site.com/ESWB4LMessaging1711043520304',
    eswBootstrapScriptSrc:
      'https://getbuilt.my.site.com/ESWB4LMessaging1711043520304/assets/js/bootstrap.min.js',
  },
  AgentforceLender: {
    salesForceKey: '00D1a000000IaVY',
    messagingChannel: 'Agentforce_Lender_Agent',
    scrt2URL: 'https://getbuilt.my.salesforce-scrt.com',
    salesForceEmbeddedServiceURL:
      'https://getbuilt.my.site.com/ESWAgentforceLenderAgen1738186009739',
    eswBootstrapScriptSrc:
      'https://getbuilt.my.site.com/ESWAgentforceLenderAgen1738186009739/assets/js/bootstrap.min.js',
  }
};

interface InitEmbeddedMessagingConfig {
  salesForceKey: string;
  messagingChannel: string;
  scrt2URL: string;
  salesForceEmbeddedServiceURL: string;
  settings?: Record<string, unknown>;
}

function initEmbeddedMessaging({
  salesForceKey,
  messagingChannel,
  scrt2URL,
  salesForceEmbeddedServiceURL,
  settings,
}: InitEmbeddedMessagingConfig): void {
  try {
    const salesForce = window.embeddedservice_bootstrap;

    if (!salesForce) {
      return;
    }

    salesForce.settings.language = 'en_US'; // For example, enter 'en' or 'en-US'
    salesForce.settings.hideChatButtonOnLoad = true; // Optional

    // Salesforce doesn't make it easy finding all possible settings
    // for now allow the user to pass in any settings they want
    Object.entries(settings || {}).forEach(([key, value]) => {
      salesForce.settings[key] = value;
    });

    salesForce.init(
      salesForceKey,
      messagingChannel,
      salesForceEmbeddedServiceURL,
      {
        scrt2URL: scrt2URL,
      }
    );
  } catch (err) {
    shipError(`Error loading Embedded Messaging`, {
      context: {
        operation: 'initEmbeddedMessaging',
        ...getCognitoCookieLogData(),
      },
      ...( err instanceof Error ? { exception: err } : {}),
    })
    error('Error loading Embedded Messaging', err);
  }
}

interface InitEmbeddedMessagingClientConfig
  extends InitEmbeddedMessagingConfig {
  eswBootstrapScriptSrc: string;
}

const initializeMessengerClient = (
  input: InitEmbeddedMessagingClientConfig
) => {
  if (window.embeddedservice_bootstrap) {
    warn('Messenger client already initialized');
    return;
  }

  const { eswBootstrapScriptSrc, ...initEmbeddedMessagingConfig } = input;

  try {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = eswBootstrapScriptSrc;
    script.onload = function (): void {
      initEmbeddedMessaging(initEmbeddedMessagingConfig);
    };
    document.body.appendChild(script);
  } catch (err) {
    shipError(`Error loading Embedded Messaging`, {
      context: {
        operation: 'initializeMessengerClient',
        ...getCognitoCookieLogData(),
      },
      ...( err instanceof Error ? { exception: err } : {}),
    })
    error('Error loading Embedded Messaging', err);
  }
};

const startMessenger = () => {
  try {
    void window.embeddedservice_bootstrap!.utilAPI.launchChat();
  } catch (err) {
    shipError(`Error starting chat`, {
      context: {
        operation: 'startMessenger',
        ...getCognitoCookieLogData(),
      },
      ...( err instanceof Error ? { exception: err } : {}),
    })
    error('Error starting chat', err);
  }
};

//  _____       _ _
// |_   _|     (_) |
//   | |  _ __  _| |_
//   | | | '_ \| | __|
//  _| |_| | | | | |_
// |_____|_| |_|_|\__|
// Exposed Utility functions to initialize and start the Salesforce client

type SalesforceClientInput =
  | {
      domain: 'B4L' | 'B4C' | 'AgentforceLender';
      settings?: Record<string, unknown>;
    }

/**
 * Initializes the Salesforce client based on the provided input.
 *
 * @param input - An object containing the domain and client type. If the client type is 'chat',
 * the input should also include the Salesforce chat configuration. If the client type is 'messenger',
 * the function will use the domain to determine the appropriate messenger configuration.
 *
 * @returns void
 */
export function initializeSalesforceClient(input: SalesforceClientInput) {
  const { domain } = input;

  const config = prodMessengerConfig[domain];

  verbose('initializing salesforce client', config)

  initializeMessengerClient({ ...config, settings: input.settings });
}

/**
 * Starts the Salesforce chat based current embedded service.
 *
 * @returns void
 */
export function startSalesforceChat() {
  verbose('starting salesforce chat utility')

  if (!window.embeddedservice_bootstrap) {
    shipError(`No embedded service detected. Cannot start chat.`, {
      context: {
        operation: 'startSalesforceChat',
        ...getCognitoCookieLogData(),
      },
    })
    error('No embedded service detected. Cannot start chat.');
    return;
  } else {
    startMessenger();
  }
}
